import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField,
    Button,
    CircularProgress,
    Alert,
    IconButton,
    Divider,
    Stack,
} from '@mui/material';
import {
    Edit,
    Save,
    Cancel,
    Email,
    Phone,
    ContactSupport,
    Refresh,
    LocationOn,
} from '@mui/icons-material';
import { getContact, updateContact, createContact } from '../../services/Api';

const Contact = () => {
    const [contactData, setContactData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [addressError, setAddressError] = useState('');
    const fetchContact = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getContact();
            if (response.success) {
                // Robust data detection
                let data = null;
                if (response.data) {
                    data = Array.isArray(response.data) ? (response.data[0] || null) : response.data;
                } else if (response.contact) {
                    data = response.contact;
                } else if (response.id || response._id) {
                    data = response;
                }

                setContactData(data);
                setFormData(data || {});
            } else {
                setError(response?.message || 'Failed to fetch contact details');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'An error occurred while fetching contact details');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContact();
    }, []);

    const handleEditToggle = () => {
        if (isEditing) {
            // Cancel edit: reset form data
            setFormData(contactData || {});
        }
        setIsEditing(!isEditing);
        setError(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'email') {
            setEmailError('');
        }
        if (name === 'phone') {
            setPhoneError('');
            const formattedValue = value.replace(/\D/g, '');
            setFormData((prev) => ({ ...prev, [name]: formattedValue }));
            return;
        }
        if (name === 'address') {
            setAddressError('');
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        const id = formData?.id || formData?._id || contactData?.id || contactData?._id;
        let hasError = false;

        // Email Validation
        if (!formData.email || !formData.email.trim()) {
            setEmailError("Email is required.");
            hasError = true;
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email.trim())) {
                setEmailError("Please enter a valid email address.");
                hasError = true;
            }
        }

        // Phone Validation
        if (!formData.phone || !formData.phone.trim()) {
            setPhoneError("Phone number is required.");
            hasError = true;
        } else if (formData.phone.length !== 10) {
            setPhoneError("Phone number must be exactly 10 digits.");
            hasError = true;
        }

        // Address Validation
        if (!formData.address || !formData.address.trim()) {
            setAddressError("Address is required.");
            hasError = true;
        } else if (/^\d+$/.test(formData.address.trim())) {
            setAddressError("Address cannot contain only numeric values.");
            hasError = true;
        } else if (/^[^a-zA-Z0-9]+$/.test(formData.address.trim())) {
            setAddressError("Address cannot contain only special characters.");
            hasError = true;
        }

        if (hasError) return;

        setSaving(true);
        setError(null);
        try {
            const response = id
                ? await updateContact(id, formData)
                : await createContact(formData);


            if (response.success) {
                const savedData = response.data || formData;
                setContactData(savedData);
                setFormData(savedData);
                setIsEditing(false);
            } else if (!response.success) {
                setError(response.message || 'Failed to update contact details');
            }
        } catch (err) {
            console.error(err);
            const backendMessage = err.response?.data?.message || err.response?.data?.error;
            setError(backendMessage || 'An error occurred while saving contact details');
        } finally {
            setSaving(false);
        }
    };

    // Removed early return for loading

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 300, textTransform: 'capitalize' }}>
                        Contact Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        View and update the official contact information for CareOff
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    <IconButton onClick={fetchContact} disabled={isEditing || loading}>
                        {loading ? <CircularProgress size={24} color="inherit" /> : <Refresh />}
                    </IconButton>
                    {!isEditing ? (
                        <Button
                            variant="contained"
                            startIcon={<Edit />}
                            onClick={handleEditToggle}
                            sx={{ px: 4, py: 1, backgroundColor: 'primary.main' }}
                        >
                            Edit Details
                        </Button>
                    ) : (
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="outlined"
                                startIcon={<Cancel />}
                                onClick={handleEditToggle}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                onClick={handleSave}
                                disabled={saving}
                                sx={{ px: 4, backgroundColor: 'primary.main' }}
                            >
                                Save Changes
                            </Button>
                        </Stack>
                    )}
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 4 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress color="primary" />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'text.border' }}>
                            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ContactSupport color="primary" /> Official Contact Info
                            </Typography>
                            <Divider sx={{ mb: 4 }} />

                            <Grid container spacing={4}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Email color="primary" />
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Support Email
                                            </Typography>
                                            {isEditing ? (
                                                <TextField
                                                    fullWidth
                                                    name="email"
                                                    value={formData.email || ''}
                                                    onChange={handleChange}
                                                    variant="standard"
                                                    error={Boolean(emailError)}
                                                    helperText={emailError}
                                                />
                                            ) : (
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    {contactData?.email || 'Not Provided'}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Phone color="primary" />
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Phone Number
                                            </Typography>
                                            {isEditing ? (
                                                <TextField
                                                    fullWidth
                                                    name="phone"
                                                    value={formData.phone || ''}
                                                    onChange={handleChange}
                                                    variant="standard"
                                                    inputProps={{ maxLength: 10 }}
                                                    error={Boolean(phoneError)}
                                                    helperText={phoneError}
                                                />
                                            ) : (
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    {contactData?.phone || 'Not Provided'}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                        <LocationOn color="primary" sx={{ mt: 0.5 }} />
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Address
                                            </Typography>
                                            {isEditing ? (
                                                <TextField
                                                    fullWidth
                                                    name="address"
                                                    value={formData.address || ''}
                                                    onChange={handleChange}
                                                    variant="standard"
                                                    multiline
                                                    rows={2}
                                                    error={Boolean(addressError)}
                                                    helperText={addressError}
                                                />
                                            ) : (
                                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                    {contactData?.address || 'Not Provided'}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Grid>
                                {/* Add more fields as needed based on API response */}
                            </Grid>
                        </Paper>
                    </Grid>

                </Grid>
            )}
        </Box>
    );
};

export default Contact;
